// ====================================================================
// BUFFET MULTIEVENTOS — Catálogo oficial em formato PACOTES PRONTOS
// ====================================================================
// Fluxo: tipo de evento → pacote (Tradicional/Premium) → personalização
// ====================================================================

import { Heart, Cake, Crown, Sparkles, Briefcase, Flame, UtensilsCrossed, Truck } from 'lucide-react';

// ===== TIPOS DE EVENTO =====
// Cada tipo aponta para os IDs de pacotes elegíveis (mapeamento N:N).
export const eventTypes = [
  { id: 'casamento',    label: 'Casamento',           icon: Heart,    packageGroup: 'casamento' },
  { id: 'aniversario',  label: 'Aniversário',         icon: Cake,     packageGroup: 'aniversario' },
  { id: 'debutante',    label: '15 anos / Debutante', icon: Crown,    packageGroup: 'debutante' },
  { id: 'infantil',     label: 'Festa Infantil',      icon: Sparkles, packageGroup: 'infantil' },
  { id: 'corporativo',  label: 'Corporativo / Happy Hour', icon: Briefcase, packageGroup: 'boteco' },
  { id: 'churrasco',    label: 'Churrasco',           icon: Flame,    packageGroup: 'churrasco' },
];

// ===== EQUIPE / STAFF PRESETS =====
const STAFF_STANDARD = [
  'Ajudante de montagem',
  'Fritadeira',
  'Cozinheiro(a)',
  'Copeiro',
  'Coordenador de Eventos',
  'Garçom',
];
const STAFF_CHURRASCO = [
  'Churrasqueiro',
  'Ajudante de montagem',
  'Chef de Cozinha',
  'Cozinheiro(a)',
  'Copeiro',
  'Coordenador de Eventos',
  'Garçom',
];

// ===== ITENS PADRÃO REUTILIZADOS =====
const ITEM_PRATO_QUENTE_TRADICIONAL = {
  id: 'prato_quente',
  label: 'Prato Quente',
  desc: '01 opção de massa + 02 opções de molhos + acompanhamentos',
  swappable: true,  // pode ser trocado por Almoço/Jantar
};
const ITEM_PRATO_QUENTE_PREMIUM = {
  id: 'prato_quente_premium',
  label: 'Prato Quente',
  desc: '01 opção de massa + 02 opções de molhos + acompanhamentos',
  swappable: true,
};

// Substituição
export const ALMOCO_JANTAR = {
  id: 'almoco_jantar',
  label: 'Almoço / Jantar (substituto do Prato Quente)',
  desc: 'Prato principal (1 ou 2 opções) + massa + guarnições + salada tropical',
  pricePerPerson: 45,
};

// ===== PACOTES =====
// Estrutura padronizada para todos:
// {
//   id, group, tier ('tradicional'|'premium'|'especial'),
//   name, eventTypes[], pricePerPerson,
//   summary, items[{ id,label,desc,swappable? }],
//   staff[], includesStructure: true,
//   highlight?: 'Mais escolhido' | 'Top premium'
// }
export const packages = [
  // ----- COMIDA DE BOTECO (Corporativo / Happy Hour) -----
  {
    id: 'boteco_tradicional',
    group: 'boteco',
    tier: 'tradicional',
    name: 'Comida de Boteco — Tradicional',
    pricePerPerson: 105,
    summary: 'Pacote de comida de boteco com petiscos, petit gourmet e prato principal.',
    items: [
      { id: 'petisco_boteco', label: 'Petisco de Boteco', desc: '08 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '05 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '01 opção' },
      { ...ITEM_PRATO_QUENTE_TRADICIONAL, desc: '01 opção (massa + molhos)' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },
  {
    id: 'boteco_premium',
    group: 'boteco',
    tier: 'premium',
    name: 'Comida de Boteco — Premium',
    pricePerPerson: 155,
    summary: 'Pacote de boteco premium com mais variedade de petiscos e finger foods.',
    items: [
      { id: 'petisco_boteco', label: 'Petisco de Boteco', desc: '12 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '10 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '03 opções' },
      { ...ITEM_PRATO_QUENTE_PREMIUM },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
    highlight: 'Top premium',
  },

  // ----- CASAMENTOS -----
  {
    id: 'casamento_tradicional',
    group: 'casamento',
    tier: 'tradicional',
    name: 'Casamento — Tradicional',
    pricePerPerson: 125.90,
    summary: 'Buffet completo de casamento com salgados, cantinho mineiro, petit gourmet, prato quente, bolos e doces.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados e fritos — 20 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '05 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '01 opção' },
      { ...ITEM_PRATO_QUENTE_TRADICIONAL },
      { id: 'bolos', label: 'Bolos', desc: '03 sabores de recheio + 01 sabor de sorvete para acompanhar' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '05 opções' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
    highlight: 'Mais escolhido',
  },
  {
    id: 'casamento_premium',
    group: 'casamento',
    tier: 'premium',
    name: 'Casamento — Premium',
    pricePerPerson: 173.90,
    summary: 'Buffet premium de casamento com finger foods, mais opções de doces e itens sofisticados.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados, frios e fritos — 20 opções' },
      { id: 'finger_foods', label: 'Finger Foods', desc: '03 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '10 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '02 opções' },
      { id: 'bolos', label: 'Bolos', desc: '03 sabores de recheio + 01 sabor de sorvete para acompanhar' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '05 opções' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },

  // ----- ANIVERSÁRIO -----
  {
    id: 'aniversario_tradicional',
    group: 'aniversario',
    tier: 'tradicional',
    name: 'Aniversário — Tradicional',
    pricePerPerson: 125.90,
    summary: 'Buffet tradicional de aniversário com salgados, prato quente, bolos e doces.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados e fritos — 20 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '05 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '01 opção' },
      { ...ITEM_PRATO_QUENTE_TRADICIONAL },
      { id: 'bolos', label: 'Bolos', desc: '03 sabores de recheio + 01 sabor de sorvete' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '05 opções' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },
  {
    id: 'aniversario_premium',
    group: 'aniversario',
    tier: 'premium',
    name: 'Aniversário — Premium',
    pricePerPerson: 173.90,
    summary: 'Buffet premium de aniversário com finger foods e mais variedade.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados, frios e fritos — 20 opções' },
      { id: 'finger_foods', label: 'Finger Foods', desc: '03 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '10 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '02 opções' },
      { id: 'bolos', label: 'Bolos', desc: '03 sabores de recheio + 01 sabor de sorvete' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '05 opções' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },

  // ----- DEBUTANTE / 15 ANOS -----
  {
    id: 'debutante_tradicional',
    group: 'debutante',
    tier: 'tradicional',
    name: '15 Anos — Tradicional',
    pricePerPerson: 125.90,
    summary: 'Buffet tradicional de debutante com salgados, prato quente, bolos e doces.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados e fritos — 20 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '05 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '01 opção' },
      { ...ITEM_PRATO_QUENTE_TRADICIONAL },
      { id: 'bolos', label: 'Bolos', desc: '03 sabores de recheio + 01 sabor de sorvete' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '05 opções' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },
  {
    id: 'debutante_premium',
    group: 'debutante',
    tier: 'premium',
    name: '15 Anos — Premium',
    pricePerPerson: 169.90,
    summary: 'Buffet premium de debutante com finger foods e cardápio sofisticado.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados, frios e fritos — 20 opções' },
      { id: 'finger_foods', label: 'Finger Foods', desc: '03 opções' },
      { id: 'cantinho_mineiro', label: 'Cantinho Mineiro', desc: '10 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet (Mini Comidinha)', desc: '02 opções' },
      { id: 'bolos', label: 'Bolos', desc: '03 sabores de recheio + 01 sabor de sorvete' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '05 opções' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },

  // ----- FESTA INFANTIL -----
  {
    id: 'infantil_tradicional',
    group: 'infantil',
    tier: 'tradicional',
    name: 'Festa Infantil — Tradicional',
    pricePerPerson: 125,
    summary: 'Pacote infantil com salgados tradicionais, comida de boteco, petit gourmet, mesa kids, bolos e doces.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados e fritos' },
      { id: 'comida_boteco_pack', label: 'Comida de Boteco', desc: '05 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet', desc: '01 opção' },
      { id: 'mesa_kids', label: 'Mesa Kids', desc: '05 opções' },
      { id: 'bolos', label: 'Bolos', desc: '01 bolo com 3 opções de recheio + sorvete acompanhamento' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '05 opções por convidado' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },
  {
    id: 'infantil_premium',
    group: 'infantil',
    tier: 'premium',
    name: 'Festa Infantil — Premium',
    pricePerPerson: 165,
    summary: 'Pacote premium infantil com finger foods, comida de boteco premium e mais variedade.',
    items: [
      { id: 'salgados', label: 'Salgados', desc: 'Assados, frios e fritos' },
      { id: 'finger_foods', label: 'Finger Foods', desc: '04 opções' },
      { id: 'comida_boteco_pack', label: 'Comida de Boteco', desc: '08 opções + torresmo, mandioca, batata frita' },
      { id: 'petit_gourmet', label: 'Petit Gourmet', desc: '03 opções' },
      { id: 'mesa_kids', label: 'Mesa Kids', desc: '05 opções' },
      { id: 'bolos', label: 'Bolos', desc: '01 bolo com 3 opções de recheio + sorvete' },
      { id: 'bombons', label: 'Bombons e Doces', desc: '08 opções por convidado' },
    ],
    staff: STAFF_STANDARD,
    includesStructure: true,
  },

  // ----- CHURRASCO -----
  {
    id: 'churrasco_tradicional',
    group: 'churrasco',
    tier: 'tradicional',
    name: 'Churrasco — Tradicional',
    pricePerPerson: 135,
    summary: 'Churrasco com melhores opções de carnes, 350g por convidado, acompanhamentos e salada.',
    items: [
      { id: 'carnes_trad', label: 'Carnes (Boi, Porco, Frango)', desc: '350g por convidado' },
      { id: 'acompanhamentos_churrasco', label: 'Acompanhamentos', desc: 'Arroz, tropeiro' },
      { id: 'salada_churrasco', label: 'Salada', desc: 'Ou vinagrete' },
    ],
    staff: STAFF_CHURRASCO,
    includesStructure: true,
  },
  {
    id: 'churrasco_premium',
    group: 'churrasco',
    tier: 'premium',
    name: 'Churrasco — Premium',
    pricePerPerson: 155,
    summary: 'Versão premium do churrasco com cortes especiais e maior variedade de acompanhamentos.',
    items: [
      { id: 'carnes_prem', label: 'Carnes Premium (Boi, Porco, Frango)', desc: '350g por convidado — cortes especiais' },
      { id: 'acompanhamentos_churrasco', label: 'Acompanhamentos', desc: 'Arroz, tropeiro, farofa especial' },
      { id: 'salada_churrasco', label: 'Salada', desc: 'Tropical ou vinagrete' },
    ],
    staff: STAFF_CHURRASCO,
    includesStructure: true,
  },
  {
    id: 'churrasco_fogo_chao',
    group: 'churrasco',
    tier: 'especial',
    name: 'Churrasco — Fogo de Chão e Parrilla',
    pricePerPerson: 189,
    summary: 'Pacote especial com cortes nobres + peixe e frutos do mar. 350g por convidado.',
    items: [
      { id: 'carnes_fogo_chao', label: 'Carnes Nobres + Peixe e Frutos do Mar', desc: 'Picanha, alcatra, costela, salmão, linguiça, lombo, medalhão de frango, mussarela, salsichão, asinha de frango, provolone com abacaxi — 350g/convidado' },
      { id: 'acompanhamentos_premium', label: 'Acompanhamentos', desc: 'Arroz, arroz carreteiro, arroz de costela, paella mineira, farofa rica, tropeiro' },
      { id: 'salada_premium', label: 'Salada', desc: 'Tropical ou vinagrete' },
    ],
    staff: STAFF_CHURRASCO,
    includesStructure: true,
    highlight: 'Mais sofisticado',
  },
];

// ===== BEBIDAS (opcional, multi-select) — preço por pessoa =====
export const drinkItems = [
  { id: 'bebidas_sem_alcool',  label: 'Bebidas sem Álcool',
    description: 'Água, refrigerante (Coca/Guaraná) e sucos — à vontade',
    pricePerPerson: 22 },
  { id: 'cerveja_tradicional', label: 'Cerveja Tradicional',
    description: 'Brahma, Stella, Skol, Itaipava, Amstel — à vontade',
    pricePerPerson: 25 },
  { id: 'cerveja_premium',     label: 'Cerveja Premium',
    description: 'Heineken, Original, Walls, Badem Badem — à vontade',
    pricePerPerson: 30 },
  { id: 'drinks_sem_alcool',   label: 'Drinks sem Álcool',
    description: 'Batidas, caips, mojito, piña colada, moscou mule — 6h',
    pricePerPerson: 30 },
  { id: 'drinks_com_alcool',   label: 'Drinks com Álcool',
    description: 'Batidas, caips, gin tônica, mojito, piña colada — 6h',
    pricePerPerson: 40 },
];

// ===== ESTRUTURA BUFFET (R$ 15/pessoa, sempre incluso no presencial) =====
export const STRUCTURE_FEE_PER_PERSON = 15;

export const structureItems = [
  'Taças', 'Pratos', 'Talheres', 'Bandejas', 'Rechauds',
  'Jarras', 'Tigelas', 'Ramequins', 'Fogão', 'Forno', 'Gás', 'Fritadeiras',
];

// Cargos genéricos (kept for backward compat / footer / PDF page 2 fallback)
export const teamRoles = [
  { role: 'Coordenador de Eventos', description: 'Garante que tudo saia conforme o planejado' },
  { role: 'Chef de Cozinha',        description: 'Elabora o cardápio e supervisiona a produção' },
  { role: 'Cozinheiro(a)',          description: 'Prepara pratos com ingredientes frescos' },
  { role: 'Churrasqueiro',          description: 'Especialista em carnes na brasa' },
  { role: 'Barman / Backer',        description: 'Prepara drinks e coquetéis' },
  { role: 'Garçom',                 description: 'Atendimento aos convidados' },
  { role: 'Copeiro',                description: 'Suporte na organização e limpeza' },
  { role: 'Ajudante de Montagem',   description: 'Montagem e desmontagem completa' },
  { role: 'Fritadeira',             description: 'Operação de fritadeira durante o evento' },
];

// ===== DELIVERY (encomenda — mantido como serviço alternativo) =====
export const deliveryItems = [
  { id: 'salgados_cento',  label: 'Salgados (cento)',          pricePerUnit: 60, unit: 'cento' },
  { id: 'bolo_kg',         label: 'Bolo (kg)',                 pricePerUnit: 90, unit: 'kg' },
  { id: 'docinhos_cento',  label: 'Docinhos (cento)',          pricePerUnit: 75, unit: 'cento' },
  { id: 'petit_gourmet',   label: 'Petit Gourmet (porção)',    pricePerUnit: 25, unit: 'porção' },
  { id: 'mini_comidinha',  label: 'Mini Comidinha (porção)',   pricePerUnit: 22, unit: 'porção' },
  { id: 'antepastos',      label: 'Antepastos / Frios (kg)',   pricePerUnit: 80, unit: 'kg' },
];

// ===== PARCEIROS (interesse — exibido na etapa "Equipe & Estrutura") =====
export const partners = [
  { id: 'decoracao',   label: 'Decoração de Festas' },
  { id: 'foto_video',  label: 'Fotografia e Vídeo' },
  { id: 'dj_som',      label: 'DJ / Som' },
  { id: 'banda_ao_vivo', label: 'Banda ao Vivo' },
  { id: 'transporte',  label: 'Transporte / Translado' },
  { id: 'cerimonial',  label: 'Cerimonial' },
];

// ===== Helpers =====
export const getPackagesForEvent = (eventTypeId) => {
  const evt = eventTypes.find((e) => e.id === eventTypeId);
  if (!evt) return [];
  return packages.filter((p) => p.group === evt.packageGroup);
};

export const getPackageById = (packageId) => packages.find((p) => p.id === packageId);

// Item lookup: retorna label legível para um ID de item
export const findItemLabel = (itemId, pkg) => {
  if (pkg) {
    const it = pkg.items.find((i) => i.id === itemId);
    if (it) return it.label;
  }
  return itemId;
};

// Legacy: buffetItems kept as empty array so older code paths don't break.
export const buffetItems = [];

// ===== GALERIA DE IMAGENS =====
export const galleryImages = [
  { src: 'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=900&q=80', alt: 'Mesa de buffet de casamento' },
  { src: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=900&q=80', alt: 'Decoração de evento' },
  { src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80', alt: 'Salgados finos' },
  { src: 'https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&w=900&q=80', alt: 'Drink especial' },
  { src: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=900&q=80', alt: 'Festa de casamento' },
  { src: 'https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=900&q=80', alt: 'Bolo de casamento' },
  { src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80', alt: 'Mesa de doces' },
  { src: 'https://images.unsplash.com/photo-1583394293214-28ded15ee548?auto=format&fit=crop&w=900&q=80', alt: 'Churrasco' },
];

// ===== DEPOIMENTOS =====
export const testimonials = [
  { name: 'Mariana Costa',  event: 'Casamento — 180 convidados', quote: 'Tudo perfeito do começo ao fim. Equipe atenciosa, comida deliciosa e estrutura impecável. Recomendo de olhos fechados.' },
  { name: 'Roberto Silva',  event: 'Aniversário — 100 convidados', quote: 'Buffet de excelência. Cardápio variado e atendimento de primeira. Meus convidados elogiaram muito.' },
  { name: 'Carla Mendes',   event: 'Debutante — 150 convidados', quote: 'Profissionalismo absoluto. A festa da minha filha foi exatamente como sonhamos. Obrigada por tudo.' },
  { name: 'João Oliveira',  event: 'Corporativo — 80 convidados', quote: 'Pontualidade e qualidade. Resolvemos um evento de última hora com excelência. Cliente fidelizado.' },
  { name: 'Patrícia Lima',  event: 'Casamento — 220 convidados', quote: 'A coordenadora foi sensacional. Tudo aconteceu como combinado. Buffet espetacular!' },
  { name: 'André Pereira',  event: 'Festa Infantil — 60 convidados', quote: 'A mesa kids fez sucesso com as crianças. Equipe muito atenciosa e cardápio adaptado para os pequenos.' },
];

// ===== NAVEGAÇÃO =====
export const navLinks = [
  { href: '#simulador',     label: 'Simulador' },
  { href: '#galeria',       label: 'Galeria' },
  { href: '#depoimentos',   label: 'Depoimentos' },
  { href: '#equipe',        label: 'Equipe' },
  { href: '#faq',           label: 'FAQ' },
];
